/**
 * Step 2: Wire Interfaces
 * Connect signal and value interfaces between selected assets.
 */
import type React from "react";
import type { WireInterfacesStepProps } from "./types";
import { useWireState, useCanvasData } from "./useWireState";
import { WireCanvas } from "./WireCanvas";
import { WireAssetCards } from "./WireAssetCards";
import { WireConnectionForm } from "./WireConnectionForm";
import { WireConnectionList } from "./WireConnectionList";

export const WireInterfacesStep: React.FC<WireInterfacesStepProps> = ({
  availableAssets,
  selectedAssetIds,
  links,
  addLink,
  removeLink,
}) => {
  const selectedAssets = availableAssets.filter((a) => selectedAssetIds.includes(a.id));

  const wireState = useWireState({
    selectedAssets,
    links,
    addLink,
  });

  const canvasData = useCanvasData(selectedAssets, links, wireState.getAssetName);

  return (
    <div>
      {/* Canvas Visualization */}
      <WireCanvas
        selectedAssets={selectedAssets}
        links={links}
        selectedLinkId={wireState.selectedLinkId}
        coreAsset={canvasData.coreAsset}
        signalConnections={canvasData.signalConnections}
        valueInAssets={canvasData.valueInAssets}
        valueOutAssets={canvasData.valueOutAssets}
        hasSignal={canvasData.hasSignal}
        hasValueIn={canvasData.hasValueIn}
        hasValueOut={canvasData.hasValueOut}
        hasCharge={canvasData.hasCharge}
        activityItems={canvasData.activityItems}
        getAssetName={wireState.getAssetName}
      />

      {/* Instructions */}
      <div className="mb-4">
        <p className="text-sm text-sa-text-muted">
          Click assets to select source → target, then choose interfaces below.
          <span className="ml-2 font-medium text-sa-success">
            {links.length} connection{links.length !== 1 ? "s" : ""}
          </span>
        </p>
      </div>

      {/* Asset Cards */}
      <WireAssetCards
        selectedAssets={selectedAssets}
        sourceAsset={wireState.sourceAsset}
        targetAsset={wireState.targetAsset}
        onAssetClick={wireState.handleAssetCardClick}
        getCardBorderClass={wireState.getCardBorderClass}
      />

      {/* Connection Form */}
      <WireConnectionForm
        selectedAssets={selectedAssets}
        sourceAsset={wireState.sourceAsset}
        setSourceAsset={wireState.setSourceAsset}
        sourceInterface={wireState.sourceInterface}
        setSourceInterface={wireState.setSourceInterface}
        targetAsset={wireState.targetAsset}
        setTargetAsset={wireState.setTargetAsset}
        targetInterface={wireState.targetInterface}
        setTargetInterface={wireState.setTargetInterface}
        isFormValid={wireState.isFormValid}
        validationError={wireState.validationError}
        getLinkType={wireState.getLinkType}
        getAssetName={wireState.getAssetName}
        onAddLink={wireState.handleAddLink}
        onClearError={wireState.clearValidationError}
      />

      {/* Connection List */}
      <WireConnectionList
        links={links}
        selectedLinkId={wireState.selectedLinkId}
        getAssetName={wireState.getAssetName}
        onSelectLink={wireState.setSelectedLinkId}
        onRemoveLink={removeLink}
      />
    </div>
  );
};

export type { WireInterfacesStepProps };
